import { TextButton } from '@/components/ui/primitives';
import { useClock, useUI } from '@/state/store';
import { isJasonAsset, useSatelliteOrbit, type OrbitView } from './store';
import { orbitFreshness, TLE_ASSET_IDS } from './source';
import { useTwin } from '../store';

const views: [OrbitView, string][] = [
  ['spacecraft', 'Spacecraft'],
  ['orbit', 'Orbit & beam'],
  ['footprint', 'Ground footprint'],
];

export function SatelliteOrbitControls() {
  const { view, beam, ready, error, reading, setView, setBeam } = useSatelliteOrbit();
  const jason = useTwin((s) => isJasonAsset(s.asset));
  const tleAsset = useTwin((s) => !!s.asset && s.asset.elementId in TLE_ASSET_IDS);
  const live = useClock((s) => s.live);
  const exploded = useTwin((s) => s.viewMode === 'exploded');
  const freshness = reading && tleAsset ? orbitFreshness(reading.epochMs, reading.timeMs) : null;
  const ageDays = reading ? Math.abs(reading.timeMs - reading.epochMs) / 86_400_000 : 0;
  return (
    <div
      data-testid="satellite-orbit-controls"
      className="w-full border-t border-hairline pt-1.5 text-[11px] text-ink-2"
    >
      <div className="flex flex-wrap items-center gap-1">
        <div role="group" aria-label="Orbital camera" className="flex flex-wrap gap-1">
          {views
            .filter(([id]) => jason || id !== 'footprint')
            .map(([id, label]) => (
              <TextButton
                key={id}
                active={view === id}
                aria-pressed={view === id}
                disabled={id !== 'spacecraft' && !ready}
                onClick={() => {
                  if (id !== 'spacecraft' && exploded) useTwin.getState().setViewMode('solid');
                  setView(id);
                  if (id === 'spacecraft') {
                    useUI.getState().select({ subsystemId: null });
                    useTwin.getState().requestCamera({ poseId: 'main' });
                  }
                }}
              >
                {!jason && id === 'orbit' ? 'Orbital context' : label}
              </TextButton>
            ))}
        </div>
        {jason && (
          <TextButton
            active={beam && !exploded}
            aria-pressed={beam && !exploded}
            disabled={exploded || !ready}
            onClick={() => setBeam(!beam)}
            title="Poseidon-3B Ku-band nominal 3 dB antenna beam"
          >
            Altimeter beam
          </TextButton>
        )}
        {!live && <TextButton onClick={() => useClock.getState().setLive()}>Return to live time</TextButton>}
        <span className={freshness === 'stale' ? 'text-warning' : 'text-ink-3'} data-testid="orbit-freshness">
          {!tleAsset
            ? 'Nominal geostationary reference · not a tracked spacecraft'
            : reading
              ? `${!live ? 'Simulation' : freshness === 'stale' ? 'Stale orbit estimate' : 'Current-time orbit estimate'} · TLE ${ageDays.toFixed(1)} d from epoch`
              : 'Orbit unavailable'}
        </span>
      </div>
      {reading && (
        <div
          className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 px-2 font-mono text-[10px] text-ink-3"
          data-testid="orbit-position"
        >
          {tleAsset && (
            <span>
              {Math.abs(reading.lat).toFixed(2)}° {reading.lat >= 0 ? 'N' : 'S'} /{' '}
              {Math.abs(reading.lon).toFixed(2)}° {reading.lon >= 0 ? 'E' : 'W'}
            </span>
          )}
          <span>{reading.altKm.toFixed(1)} km altitude</span>
          {jason ? (
            <>
              <span>Ku beam ≈ {reading.diameterKm.toFixed(1)} km across</span>
              <span>{new Date(reading.timeMs).toISOString().slice(11, 19)} UTC</span>
            </>
          ) : (
            <span>Earth radius and separation rendered in metres</span>
          )}
        </div>
      )}
      {error && (
        <p role="status" className="mt-1 px-2 text-warning">
          {error}
        </p>
      )}
      <details className="mt-1 px-2 text-[10px] text-ink-3">
        <summary className="cursor-pointer">
          {jason ? 'Orbit & measurement context' : 'About this orbital reference'}
        </summary>
        {!tleAsset ? (
          <p className="mt-1 max-w-2xl leading-relaxed">
            This source model identifies a satellite family, not an individual vehicle. Earth is placed at the
            nominal 35,786 km geostationary altitude using WGS84. The reference meridian, daylight lighting
            and body orientation are illustrative; no current longitude, measured attitude or instrument
            footprint is asserted. The spacecraft retains its source proportions. Use Orbital context to
            compare the complete orbit and Earth at their physical separation; the spacecraft becomes a point
            symbol. Imagery is not live.{' '}
            <a
              className="underline"
              href="https://www.nasa.gov/missions/tdrs/tracking-and-data-relay-satellite-tdrs-generations-of-spacecraft/"
              target="_blank"
              rel="noreferrer"
            >
              Orbit reference
            </a>
          </p>
        ) : !jason ? (
          <p className="mt-1 max-w-2xl leading-relaxed">
            {reading?.source}. Position follows the shared clock using SGP4 and the displayed TLE epoch. Body
            orientation is illustrative; no measured attitude or instrument footprint is asserted. The source
            model uses inspection fill lighting. Orbital context uses a point symbol at the spacecraft
            position; Earth imagery is not live.
          </p>
        ) : (
          <p className="mt-1 max-w-2xl leading-relaxed">
            {reading
              ? `${reading.source}. TLE epoch ${new Date(reading.epochMs).toISOString().replace('T', ' ')}. `
              : ''}
            Position is an SGP4 estimate from the loader’s TLE, following the shared clock. Nominal
            nadir-pointing attitude; live pointing and instrument activity are unavailable. The translucent
            cone shows the 1.28° Ku-band antenna beam intersecting WGS84, not the smaller pulse-limited
            measurement footprint or a confirmed observation. Satellite imagery falls back to the bundled
            Natural Earth map offline; imagery is not live. The close view preserves the source model’s
            proportions with inspection fill lighting; the orbit view uses a point symbol at its position.
            {exploded && ' Beam hidden while the model is exploded.'}{' '}
            <a
              className="underline"
              href="https://www.aviso.altimetry.fr/en/missions/current-missions/jason-3/instruments/poseidon-3b.html"
              target="_blank"
              rel="noreferrer"
            >
              Instrument specification
            </a>
          </p>
        )}
      </details>
    </div>
  );
}
